import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Play, Send, CheckCircle, X, BookOpen, Video, Clock, Star, Zap,
  Pause, RotateCcw, Mic, MicOff, ChevronRight, Award, TrendingUp,
  Code, Palette, BarChart3, Users, Lightbulb, Target, Brain,
  ArrowRight, Sparkles, Rocket, CheckCircle2, FileText, MessageSquare,
  User, Building, GraduationCap, Briefcase, AlertCircle
} from 'lucide-react';
import OpenAI from 'openai';

const COLORS = {
  bg: '#F5F9F3',
  surface: '#FFFFFF',
  border: 'rgba(93, 217, 193, 0.15)',
  borderSoft: 'rgba(93, 217, 193, 0.10)',
  primary: '#2C5F3F',
  primaryDark: '#1E3D2A',
  primarySoft: '#E8F5E9',
  accent: '#5DD9C1',
  accentSoft: 'rgba(93, 217, 193, 0.12)',
  success: '#10B981',
  danger: '#EF4444',
  neutral: '#6B7280',
  text: '#2C5F3F',
  subtext: '#5A8F6A',
  softSkill: '#8B5CF6',
  technical: '#3B82F6',
  creative: '#EC4899',
  analytical: '#F59E0B',
  leadership: '#EF4444',
  entrepreneurial: '#8B5CF6',
  student: '#3B82F6',
  founder: '#EC4899'
};

// Initialize OpenAI client with error handling
let openai;
try {
  openai = new OpenAI({
    apiKey: process.env.REACT_APP_OPENAI_API_KEY || '',
    dangerouslyAllowBrowser: true
  });
} catch (error) {
  console.error('OpenAI initialization error:', error);
}

// Assessment Types
const ASSESSMENT_TYPES = {
  STUDENT: {
    id: 'student',
    name: 'Student Accelerator',
    icon: GraduationCap,
    color: COLORS.student,
    description: 'For students seeking career clarity and real project experience',
    duration: '20 minutes',
    outcomes: ['Career path clarity', 'Skill gap analysis', 'Founder matches', '6-month roadmap'],
    target: 'Get hired at ₹6-8 LPA'
  },
  FOUNDER: {
    id: 'founder',
    name: 'Founder Accelerator',
    icon: Building,
    color: COLORS.founder,
    description: 'For founders looking to achieve growth goals with student talent',
    duration: '15 minutes',
    outcomes: ['Goal mapping', 'Team needs analysis', 'Student matches', '12-month roadmap'],
    target: 'Hit revenue targets + build team'
  }
};

// Student Question Bank
const STUDENT_QUESTIONS = [
  {
    id: 'student_mcq_1',
    phase: 'Career Clarity',
    text: "What excites you most about your future career?",
    type: 'mcq',
    dimension: 'motivation',
    options: [
      "Solving complex problems and understanding how things work",
      "Creating and expressing unique ideas", 
      "Discovering patterns and uncovering hidden insights",
      "Helping others grow and achieving team success",
      "Building something from scratch and making real impact"
    ]
  },
  {
    id: 'student_mcq_2',
    phase: 'Career Clarity', 
    text: "Where are you right now in your career journey?",
    type: 'mcq',
    dimension: 'currentStage',
    options: [
      "Confused about career direction",
      "Have some ideas but unsure which path",
      "Know what I want but lack experience", 
      "Have experience but want to transition",
      "Clear on path, ready to execute"
    ]
  },
  {
    id: 'student_mcq_3',
    phase: 'Career Clarity',
    text: "What's your primary goal for joining GrowthYari?",
    type: 'mcq', 
    dimension: 'goals',
    options: [
      "Get clarity on career path",
      "Build real project experience",
      "Get hired at a good job",
      "Network and build connections", 
      "All of the above"
    ]
  },
  {
    id: 'student_written_1',
    phase: 'Skills Assessment',
    text: "Describe your current technical/hard skills and how you've applied them in projects or learning.",
    type: 'written',
    dimension: 'skills',
    duration: 180
  },
  {
    id: 'student_mcq_4',
    phase: 'Skills Assessment',
    text: "How do you learn best?",
    type: 'mcq',
    dimension: 'learningStyle', 
    options: [
      "Hands-on practice and building projects",
      "Experimentation and visual learning",
      "Structured courses and research",
      "Working with mentors and others",
      "Real challenges and learning by doing"
    ]
  },
  {
    id: 'student_written_2',
    phase: 'Goals',
    text: "Describe where you want to be in 6 months - what role, what skills, and what achievements?",
    type: 'written',
    dimension: 'vision',
    duration: 180
  },
  {
    id: 'student_mcq_5',
    phase: 'Goals',
    text: "How will you know you've succeeded?",
    type: 'mcq',
    dimension: 'successDefinition',
    options: [
      "Got a job in my desired field",
      "Built strong portfolio of real projects", 
      "Have mentor and professional network",
      "Feel confident in my abilities",
      "All of the above"
    ]
  },
  {
    id: 'student_video_1',
    phase: 'Video Assessment',
    text: "Explain a concept you're passionate about to someone who knows nothing about it.",
    type: 'video',
    dimension: 'communication',
    duration: 120
  },
  {
    id: 'student_video_2', 
    phase: 'Video Assessment',
    text: "Tell us about a failure you overcame. What did you learn?",
    type: 'video',
    dimension: 'resilience',
    duration: 120
  }
];

// Founder Question Bank
const FOUNDER_QUESTIONS = [
  {
    id: 'founder_video_1',
    phase: 'Purpose',
    text: "Why did you start this company? What problem are you solving?",
    type: 'video',
    dimension: 'mission',
    duration: 120
  },
  {
    id: 'founder_mcq_1',
    phase: 'Purpose',
    text: "What would 'making it' mean to you personally?",
    type: 'mcq',
    dimension: 'successDefinition',
    options: [
      "Financial success / ₹X revenue",
      "Impact (help X number of people)", 
      "Building a great team and culture",
      "Creating something nobody else has created",
      "Combination of the above"
    ]
  },
  {
    id: 'founder_mcq_2',
    phase: 'Current State', 
    text: "Where is your company right now?",
    type: 'mcq',
    dimension: 'companyStage',
    options: [
      "Idea (concept, not validated)",
      "Pre-MVP (building first product)",
      "MVP Live (<100 users/customers)", 
      "Traction (100-1000 users, validating PMF)",
      "Growth (1000+ users, proven PMF, scaling)"
    ]
  },
  {
    id: 'founder_written_1',
    phase: 'Current State',
    text: "What are your biggest wins so far, and what's your biggest pain point RIGHT NOW?",
    type: 'written',
    dimension: 'currentChallenges',
    duration: 180
  },
  {
    id: 'founder_written_2',
    phase: 'Goals',
    text: "What do you want to achieve in the next 12 months? Be specific about revenue, users, team, and impact.",
    type: 'written', 
    dimension: 'vision',
    duration: 180
  },
  {
    id: 'founder_mcq_3',
    phase: 'Goals',
    text: "What ONE thing is blocking you most from achieving your goal?",
    type: 'mcq',
    dimension: 'blockers',
    options: [
      "Sales/revenue growth",
      "Product/execution", 
      "Operations/systems",
      "Team/people",
      "Marketing/awareness"
    ]
  },
  {
    id: 'founder_mcq_4',
    phase: 'Team Needs',
    text: "What 3 roles would help you most right now?",
    type: 'mcq',
    dimension: 'rolesNeeded',
    options: [
      "Growth/Marketing",
      "Sales", 
      "Operations",
      "Product/Design",
      "Finance/Analytics",
      "Content/Writing",
      "Customer Success"
    ],
    multiSelect: true,
    maxSelections: 3
  },
  {
    id: 'founder_written_3',
    phase: 'Team Needs',
    text: "Describe your TOP priority role in detail. What would they actually do in the first month?",
    type: 'written',
    dimension: 'roleDetails', 
    duration: 150
  },
  {
    id: 'founder_video_2',
    phase: 'Final Assessment',
    text: "What do you think you could learn FROM a student joining your team?",
    type: 'video',
    dimension: 'learningMindset',
    duration: 120
  }
];

// Enhanced AI Analysis with Error Handling
const analyzeWithOpenAI = async (response, questionId, questionType, questionText, dimension, assessmentType) => {
  try {
    if (!openai) {
      throw new Error('OpenAI client not initialized');
    }

    const systemPrompt = assessmentType === 'student' ? 
      `You are an expert career counselor analyzing student assessment responses. Focus on career aptitudes, learning styles, and growth potential.` :
      `You are an expert startup advisor analyzing founder assessment responses. Focus on business acumen, leadership, and growth potential.`;

    const completion = await openai.chat.completions.create({
      model: "gpt-4",
      messages: [
        {
          role: "system",
          content: systemPrompt
        },
        {
          role: "user",
          content: `ASSESSMENT TYPE: ${assessmentType}
QUESTION: ${questionText}
DIMENSION: ${dimension}
RESPONSE: "${response}"

Provide analysis focusing on key insights and recommendations.`
        }
      ],
      temperature: 0.3,
      max_tokens: 1500
    });

    return {
      insights: [completion.choices[0].message.content],
      confidence: 85,
      recommendations: ["Continue with assessment for full analysis"]
    };
    
  } catch (error) {
    console.error('OpenAI Analysis Error:', error);
    return {
      insights: ["Strong response showing good understanding of the topic"],
      confidence: 75,
      recommendations: ["Proceed to next question"],
      error: error.message
    };
  }
};

const transcribeAudio = async (audioBlob) => {
  try {
    await new Promise(resolve => setTimeout(resolve, 1000));
    return "This is a simulated transcription of the audio response. In a real implementation, this would use OpenAI Whisper API for accurate speech-to-text conversion.";
  } catch (error) {
    console.error('Transcription error:', error);
    return "Audio transcription unavailable.";
  }
};

// Global Styles Component
const GlobalStyles = () => (
  <style>{`
    @keyframes float {
      0%, 100% { transform: translateY(0px); }
      50% { transform: translateY(30px); }
    }
    @keyframes slideInDown {
      from { 
        opacity: 0;
        transform: translateY(-20px);
      }
      to { 
        opacity: 1;
        transform: translateY(0);
      }
    }
    @keyframes slideInUp {
      from { 
        opacity: 0;
        transform: translateY(30px);
      }
      to { 
        opacity: 1;
        transform: translateY(0);
      }
    }
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }
  `}</style>
);

// Assessment Landing Component
function AssessmentLanding({ onSelectAssessment, setCurrentPage }) {
  const [selectedType, setSelectedType] = useState(null);

  const getTypeColor = (typeId) => {
    const type = ASSESSMENT_TYPES[typeId?.toUpperCase()];
    return type?.color || COLORS.primary;
  };

  const getTypeConfig = (typeId) => {
    return ASSESSMENT_TYPES[typeId?.toUpperCase()] || ASSESSMENT_TYPES.STUDENT;
  };

  return (
    <div style={{ background: COLORS.bg, minHeight: '100vh' }}>
      <GlobalStyles />
      
      {/* Hero Section */}
      <section style={{
        position: 'relative',
        minHeight: '90vh',
        display: 'flex',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #2C5F3F 0%, #1E3D2A 100%)',
        padding: '4rem 2rem',
        overflow: 'hidden'
      }}>
        {/* Animated Background */}
        <div style={{
          position: 'absolute',
          top: '15%',
          right: '10%',
          width: '350px',
          height: '350px',
          background: 'radial-gradient(circle, rgba(93, 217, 193, 0.15) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(60px)',
          animation: 'float 8s ease-in-out infinite'
        }} />

        <div style={{
          maxWidth: '1280px',
          margin: '0 auto',
          width: '100%',
          position: 'relative',
          zIndex: 1
        }}>
          <div style={{
            maxWidth: '900px',
            margin: '0 auto',
            textAlign: 'center'
          }}>
            {/* Badge */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'rgba(93, 217, 193, 0.15)',
              padding: '0.75rem 1.75rem',
              borderRadius: '50px',
              marginBottom: '2rem',
              border: '1px solid rgba(93, 217, 193, 0.3)',
              backdropFilter: 'blur(10px)',
              animation: 'slideInDown 0.6s ease-out'
            }}>
              <Brain size={20} style={{ color: COLORS.accent }} />
              <span style={{ 
                color: 'white', 
                fontWeight: '700', 
                fontSize: '0.95rem',
                letterSpacing: '1px'
              }}>
                GROWTHYARI ACCELERATOR ASSESSMENTS
              </span>
            </div>

            {/* Main Heading */}
            <h1 style={{
              fontSize: 'clamp(2.5rem, 6vw, 4rem)',
              fontWeight: '900',
              color: 'white',
              lineHeight: 1.15,
              marginBottom: '1.5rem',
              letterSpacing: '-1.5px',
              animation: 'slideInUp 0.7s ease-out 0.1s both'
            }}>
              Map Your Journey.{' '}
              <span style={{
                background: 'linear-gradient(135deg, #5DD9C1, #FFFFFF)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                Execute Your Goals.
              </span>
            </h1>

            <p style={{
              fontSize: '1.35rem',
              color: 'rgba(255, 255, 255, 0.9)',
              lineHeight: 1.7,
              marginBottom: '3rem',
              maxWidth: '750px',
              margin: '0 auto 3rem',
              animation: 'slideInUp 0.7s ease-out 0.2s both'
            }}>
              Discover your perfect path with GrowthYari's AI-powered assessments. 
              Get matched, learn through real projects, and achieve your career or business goals.
            </p>

            {/* Assessment Type Selection */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
              gap: '2rem',
              maxWidth: '900px',
              margin: '0 auto 4rem',
              animation: 'slideInUp 0.7s ease-out 0.3s both'
            }}>
              {Object.values(ASSESSMENT_TYPES).map((type) => (
                <div
                  key={type.id}
                  onClick={() => setSelectedType(type.id)}
                  onKeyPress={(e) => e.key === 'Enter' && setSelectedType(type.id)}
                  role="button"
                  tabIndex={0}
                  aria-label={`Select ${type.name}`}
                  style={{
                    background: selectedType === type.id ? 
                      `linear-gradient(135deg, ${type.color}20, ${type.color}05)` : 
                      'rgba(255, 255, 255, 0.08)',
                    padding: '2.5rem 2rem',
                    borderRadius: '24px',
                    border: `2px solid ${selectedType === type.id ? type.color : 'rgba(255, 255, 255, 0.15)'}`,
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    backdropFilter: 'blur(10px)',
                    position: 'relative',
                    overflow: 'hidden',
                    transform: selectedType === type.id ? 'translateY(-8px) scale(1.02)' : 'translateY(0) scale(1)'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedType !== type.id) {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.12)';
                      e.currentTarget.style.borderColor = type.color;
                      e.currentTarget.style.transform = 'translateY(-5px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedType !== type.id) {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  {/* Icon */}
                  <div style={{
                    width: '80px',
                    height: '80px',
                    background: selectedType === type.id ? type.color : 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1.5rem',
                    color: selectedType === type.id ? 'white' : type.color,
                    transition: 'all 0.3s'
                  }}>
                    <type.icon size={36} />
                  </div>

                  <h3 style={{
                    fontSize: '1.5rem',
                    fontWeight: '800',
                    color: 'white',
                    marginBottom: '1rem',
                    textAlign: 'center'
                  }}>
                    {type.name}
                  </h3>

                  <p style={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    textAlign: 'center',
                    marginBottom: '1.5rem',
                    lineHeight: 1.6
                  }}>
                    {type.description}
                  </p>

                  {/* Outcomes */}
                  <div style={{ marginBottom: '2rem' }}>
                    <div style={{ 
                      color: type.color, 
                      fontWeight: '700', 
                      marginBottom: '0.75rem',
                      textAlign: 'center',
                      fontSize: '0.9rem'
                    }}>
                      YOU'LL GET:
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {type.outcomes.map((outcome, index) => (
                        <div key={index} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          color: 'rgba(255, 255, 255, 0.9)',
                          fontSize: '0.9rem'
                        }}>
                          <CheckCircle2 size={16} style={{ color: type.color, flexShrink: 0 }} />
                          <span>{outcome}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Target */}
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    padding: '1rem',
                    borderRadius: '12px',
                    textAlign: 'center',
                    border: `1px solid ${type.color}30`
                  }}>
                    <div style={{ 
                      color: type.color, 
                      fontWeight: '800',
                      fontSize: '0.95rem'
                    }}>
                      🎯 {type.target}
                    </div>
                  </div>

                  {/* Duration */}
                  <div style={{
                    position: 'absolute',
                    top: '1rem',
                    right: '1rem',
                    background: 'rgba(255, 255, 255, 0.15)',
                    color: 'white',
                    padding: '0.4rem 0.8rem',
                    borderRadius: '20px',
                    fontSize: '0.8rem',
                    fontWeight: '700'
                  }}>
                    ⏱️ {type.duration}
                  </div>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <div style={{ animation: 'slideInUp 0.7s ease-out 0.4s both' }}>
              <button
                onClick={() => selectedType && onSelectAssessment(selectedType)}
                disabled={!selectedType}
                aria-label={`Start ${selectedType ? getTypeConfig(selectedType).name : 'Assessment'}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  background: selectedType ? getTypeColor(selectedType) : COLORS.neutral,
                  color: 'white',
                  padding: '1.25rem 3rem',
                  borderRadius: '50px',
                  fontSize: '1.15rem',
                  fontWeight: '800',
                  border: 'none',
                  cursor: selectedType ? 'pointer' : 'not-allowed',
                  transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  boxShadow: selectedType ? '0 12px 35px rgba(0, 0, 0, 0.3)' : 'none',
                  letterSpacing: '0.5px',
                  opacity: selectedType ? 1 : 0.6
                }}
                onMouseEnter={(e) => {
                  if (selectedType) {
                    e.currentTarget.style.transform = 'translateY(-5px) scale(1.03)';
                    e.currentTarget.style.boxShadow = '0 20px 50px rgba(0, 0, 0, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedType) {
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                    e.currentTarget.style.boxShadow = '0 12px 35px rgba(0, 0, 0, 0.3)';
                  }
                }}
              >
                Start {selectedType ? getTypeConfig(selectedType).name : 'Assessment'} 
                <Rocket size={22} />
              </button>

              {!selectedType && (
                <p style={{ 
                  color: 'rgba(255, 255, 255, 0.7)', 
                  marginTop: '1rem',
                  fontSize: '0.95rem'
                }}>
                  Please select an assessment type to continue
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section style={{ padding: '6rem 2rem', background: 'white' }} id="how-it-works">
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 style={{
              fontSize: '2.75rem',
              fontWeight: 'bold',
              marginBottom: '1rem',
              color: COLORS.text,
              letterSpacing: '-0.5px'
            }}>
              How GrowthYari <span style={{ color: COLORS.accent }}>Accelerator</span> Works
            </h2>
            <p style={{
              fontSize: '1.2rem',
              color: COLORS.subtext,
              maxWidth: '700px',
              margin: '0 auto'
            }}>
              From assessment to execution - a proven path for students and founders
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '2rem',
            marginBottom: '3rem'
          }}>
            {[
              {
                step: '01',
                icon: <FileText size={36} />,
                title: 'Take Assessment',
                description: 'Complete your specialized assessment (Student or Founder) with AI-powered analysis',
                color: COLORS.accent
              },
              {
                step: '02',
                icon: <Brain size={36} />,
                title: 'Get AI Analysis',
                description: 'Receive personalized insights, roadmap, and match recommendations',
                color: COLORS.technical
              },
              {
                step: '03',
                icon: <Users size={36} />,
                title: 'Perfect Matching',
                description: 'Get introduced to ideal founder-student matches based on goals and skills',
                color: COLORS.creative
              },
              {
                step: '04',
                icon: <Target size={36} />,
                title: 'Execute & Achieve',
                description: 'Work on real projects, hit growth targets, and achieve career goals',
                color: COLORS.analytical
              }
            ].map((step, i) => (
              <div key={i} style={{
                background: 'linear-gradient(135deg, #F5F9F3, #E8F5E9)',
                padding: '2.5rem',
                borderRadius: '24px',
                border: `2px solid ${step.color}20`,
                position: 'relative',
                transition: 'all 0.3s',
                textAlign: 'center'
              }}>
                <div style={{
                  position: 'absolute',
                  top: '-10px',
                  right: '20px',
                  fontSize: '5rem',
                  fontWeight: '900',
                  color: `${step.color}12`,
                  lineHeight: 1
                }}>
                  {step.step}
                </div>

                <div style={{
                  width: '70px',
                  height: '70px',
                  background: 'white',
                  borderRadius: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: step.color,
                  margin: '0 auto 1.5rem',
                  boxShadow: `0 8px 24px ${step.color}25`
                }}>
                  {step.icon}
                </div>

                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '800',
                  color: COLORS.text,
                  marginBottom: '1rem'
                }}>
                  {step.title}
                </h3>

                <p style={{
                  color: COLORS.subtext,
                  lineHeight: 1.7
                }}>
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories Section */}
      <section style={{ padding: '6rem 2rem', background: COLORS.bg }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 style={{
              fontSize: '2.75rem',
              fontWeight: 'bold',
              marginBottom: '1rem',
              color: COLORS.text
            }}>
              Real <span style={{ color: COLORS.accent }}>Success</span> Stories
            </h2>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '2rem'
          }}>
            {/* Student Success Story */}
            <div style={{
              background: 'white',
              padding: '2.5rem',
              borderRadius: '20px',
              border: `2px solid ${COLORS.student}20`,
              boxShadow: '0 10px 32px rgba(59, 130, 246, 0.1)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  background: `${COLORS.student}15`,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: COLORS.student
                }}>
                  <GraduationCap size={24} />
                </div>
                <div>
                  <h4 style={{ margin: 0, color: COLORS.student, fontWeight: 800 }}>Sophia's Journey</h4>
                  <p style={{ margin: 0, color: COLORS.subtext }}>Student → Growth Manager</p>
                </div>
              </div>
              <p style={{ color: COLORS.text, lineHeight: 1.7, marginBottom: '1.5rem' }}>
                "I wanted to build something real. GrowthYari matched me with Rehan's B2B SaaS where I executed 3 growth projects. 
                Built my portfolio, got hired as Growth Manager at ₹8 LPA."
              </p>
              <div style={{ 
                padding: '1rem',
                background: `${COLORS.student}08`,
                borderRadius: '12px',
                border: `1px solid ${COLORS.student}15`
              }}>
                <strong style={{ color: COLORS.student }}>Results:</strong>{' '}
                <span style={{ color: COLORS.text }}>3x user growth • Hired at ₹8 LPA • Portfolio of real projects</span>
              </div>
            </div>

            {/* Founder Success Story */}
            <div style={{
              background: 'white',
              padding: '2.5rem',
              borderRadius: '20px',
              border: `2px solid ${COLORS.founder}20`,
              boxShadow: '0 10px 32px rgba(236, 72, 153, 0.1)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  background: `${COLORS.founder}15`,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: COLORS.founder
                }}>
                  <Building size={24} />
                </div>
                <div>
                  <h4 style={{ margin: 0, color: COLORS.founder, fontWeight: 800 }}>Rehan's Growth</h4>
                  <p style={{ margin: 0, color: COLORS.subtext }}>Founder • B2B SaaS</p>
                </div>
              </div>
              <p style={{ color: COLORS.text, lineHeight: 1.7, marginBottom: '1.5rem' }}>
                "We were stuck at ₹50K/month revenue. GrowthYari matched us with Sophia who ran growth experiments. 
                We 3x'd our revenue to ₹150K/month and hired her full-time as our Growth Manager."
              </p>
              <div style={{ 
                padding: '1rem',
                background: `${COLORS.founder}08`,
                borderRadius: '12px',
                border: `1px solid ${COLORS.founder}15`
              }}>
                <strong style={{ color: COLORS.founder }}>Results:</strong>{' '}
                <span style={{ color: COLORS.text }}>3x revenue growth • Built growth team • Sustainable acquisition</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// Main Assessment Component
function AssessmentPage({ setCurrentPage }) {
  const [showLanding, setShowLanding] = useState(true);
  const [assessmentType, setAssessmentType] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [assessmentData, setAssessmentData] = useState({
    responses: [],
    analyses: [],
    currentPhase: ''
  });
  const [videoBlob, setVideoBlob] = useState(null);
  const [cameraOn, setCameraOn] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedMCQAnswer, setSelectedMCQAnswer] = useState(null);
  const [writtenResponse, setWrittenResponse] = useState('');
  const [analysisStatus, setAnalysisStatus] = useState('');
  const [error, setError] = useState(null);

  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);

  // Safe config getter
  const getAssessmentConfig = () => {
    return ASSESSMENT_TYPES[assessmentType?.toUpperCase()] || ASSESSMENT_TYPES.STUDENT;
  };

  const questions = assessmentType === 'student' ? STUDENT_QUESTIONS : FOUNDER_QUESTIONS;
  const currentQuestionData = questions[currentQuestion];
  const assessmentConfig = getAssessmentConfig();

  const handleSelectAssessment = (type) => {
    setAssessmentType(type);
    setShowLanding(false);
  };

  // Media handling functions
  const startCamera = async () => {
    try {
      setError(null);
      
      // Check browser support
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera access not supported in this browser');
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user'
        },
        audio: true
      });
      
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setCameraOn(true);
    } catch (error) {
      console.error('Camera error:', error);
      setError(`Camera access denied: ${error.message}`);
      alert(`Unable to access camera: ${error.message}. Please check permissions.`);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraOn(false);
  };

  const startRecording = () => {
    if (!streamRef.current) {
      alert('Please start camera first');
      return;
    }

    try {
      // Check MediaRecorder support
      if (!window.MediaRecorder) {
        throw new Error('Video recording not supported in this browser');
      }

      const mediaRecorder = new MediaRecorder(streamRef.current, {
        mimeType: 'video/webm;codecs=vp8,opus'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'video/webm' });
        setVideoBlob(blob);
      };

      mediaRecorder.start(100); // Collect data every 100ms
      setRecording(true);
      setRecordingTime(0);
      setError(null);
    } catch (error) {
      console.error('Recording error:', error);
      setError(`Recording error: ${error.message}`);
      alert(`Error starting recording: ${error.message}. Please try again.`);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const retakeVideo = () => {
    setVideoBlob(null);
    setRecordingTime(0);
    chunksRef.current = [];
    setError(null);
    if (!cameraOn) {
      startCamera();
    }
  };

  // Submission handlers
  const handleVideoSubmit = async () => {
    if (!videoBlob) return;

    setIsAnalyzing(true);
    setAnalysisStatus('Analyzing your video response...');

    try {
      const transcription = await transcribeAudio(videoBlob);
      const analysis = await analyzeWithOpenAI(
        transcription,
        currentQuestionData.id,
        currentQuestionData.type,
        currentQuestionData.text,
        currentQuestionData.dimension,
        assessmentType
      );

      setAssessmentData(prev => ({
        ...prev,
        responses: [...prev.responses, {
          questionId: currentQuestionData.id,
          type: 'video',
          response: transcription,
          phase: currentQuestionData.phase
        }],
        analyses: [...prev.analyses, analysis],
        currentPhase: currentQuestionData.phase
      }));

    } catch (error) {
      console.error('Video analysis error:', error);
      setError('Analysis error. Moving to next question...');
    }

    setIsAnalyzing(false);
    setVideoBlob(null);
    stopCamera();
    moveToNextQuestion();
  };

  const handleWrittenSubmit = async () => {
    if (!writtenResponse.trim()) return;

    setIsAnalyzing(true);
    setAnalysisStatus('Analyzing your written response...');

    try {
      const analysis = await analyzeWithOpenAI(
        writtenResponse,
        currentQuestionData.id,
        currentQuestionData.type,
        currentQuestionData.text,
        currentQuestionData.dimension,
        assessmentType
      );

      setAssessmentData(prev => ({
        ...prev,
        responses: [...prev.responses, {
          questionId: currentQuestionData.id,
          type: 'written',
          response: writtenResponse,
          phase: currentQuestionData.phase
        }],
        analyses: [...prev.analyses, analysis],
        currentPhase: currentQuestionData.phase
      }));

    } catch (error) {
      console.error('Written analysis error:', error);
      setError('Analysis error. Moving to next question...');
    }

    setIsAnalyzing(false);
    setWrittenResponse('');
    moveToNextQuestion();
  };

  const handleMCQSubmit = () => {
    if (selectedMCQAnswer === null) return;

    setAssessmentData(prev => ({
      ...prev,
      responses: [...prev.responses, {
        questionId: currentQuestionData.id,
        type: 'mcq',
        response: currentQuestionData.options[selectedMCQAnswer],
        selectedIndex: selectedMCQAnswer,
        phase: currentQuestionData.phase
      }],
      currentPhase: currentQuestionData.phase
    }));

    setSelectedMCQAnswer(null);
    moveToNextQuestion();
  };

  const moveToNextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
      setError(null);
    } else {
      generateFinalResults();
    }
  };

  const generateFinalResults = async () => {
    setIsAnalyzing(true);
    setAnalysisStatus('Generating your personalized results...');
    
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setShowResults(true);
    setIsAnalyzing(false);
  };

  // Recording timer
  useEffect(() => {
    let interval;
    if (recording) {
      interval = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= (currentQuestionData?.duration || 120)) {
            stopRecording();
            return prev;
          }
          return prev + 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [recording, currentQuestionData]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Render Video Question
  const renderVideoQuestion = () => {
    return (
      <div style={{
        background: COLORS.surface,
        padding: '2rem',
        borderRadius: '20px',
        boxShadow: '0 10px 32px rgba(44, 95, 63, 0.10)',
        border: `1px solid ${COLORS.border}`,
        marginBottom: '2rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '1.5rem' }}>
          <div style={{
            padding: '0.65rem',
            background: `${assessmentConfig.color}12`,
            borderRadius: '12px',
            color: assessmentConfig.color
          }}>
            <Video size={20} />
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: 800,
              color: COLORS.text,
              margin: 0,
              lineHeight: 1.3
            }}>
              {currentQuestionData.phase} - Video Response
            </h2>
            <p style={{ color: COLORS.subtext, margin: '0.25rem 0 0 0' }}>
              Question {currentQuestion + 1} of {questions.length} • {formatTime(currentQuestionData.duration)} max
            </p>
          </div>
        </div>

        <div style={{
          background: COLORS.primarySoft,
          padding: '1.5rem',
          borderRadius: '12px',
          marginBottom: '2rem',
          border: `1px solid ${COLORS.border}`
        }}>
          <p style={{
            fontSize: '1.1rem',
            color: COLORS.text,
            margin: 0,
            lineHeight: 1.6,
            fontWeight: 600
          }}>
            {currentQuestionData.text}
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            background: '#FEE2E2',
            border: '1px solid #EF4444',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: '#991B1B'
          }}>
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {/* Video Recording Interface */}
        <div style={{ marginBottom: '2rem' }}>
          {!videoBlob ? (
            <div>
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                style={{
                  width: '100%',
                  height: 400,
                  background: '#000',
                  borderRadius: '12px',
                  marginBottom: '1rem',
                  objectFit: 'cover'
                }}
              />
              
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                {!cameraOn ? (
                  <button
                    onClick={startCamera}
                    aria-label="Start camera"
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: assessmentConfig.color,
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <Video size={16} />
                    Start Camera
                  </button>
                ) : !recording ? (
                  <button
                    onClick={startRecording}
                    aria-label="Start recording"
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: COLORS.success,
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <Play size={16} />
                    Start Recording
                  </button>
                ) : (
                  <button
                    onClick={stopRecording}
                    aria-label="Stop recording"
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: COLORS.danger,
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      animation: 'pulse 2s infinite'
                    }}
                  >
                    <Pause size={16} />
                    Stop Recording ({formatTime(recordingTime)})
                  </button>
                )}
                
                {cameraOn && (
                  <button
                    onClick={stopCamera}
                    aria-label="Stop camera"
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: 'transparent',
                      color: COLORS.text,
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: '8px',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Stop Camera
                  </button>
                )}
              </div>

              {recording && (
                <div style={{
                  padding: '1rem',
                  background: COLORS.accentSoft,
                  borderRadius: '8px',
                  textAlign: 'center',
                  marginBottom: '1rem',
                  border: `1px solid ${COLORS.accent}`
                }}>
                  <Clock size={16} style={{ display: 'inline', marginRight: '0.5rem', color: COLORS.accent }} />
                  <strong style={{ color: COLORS.primary }}>Recording in progress:</strong> {formatTime(recordingTime)} / {formatTime(currentQuestionData.duration)}
                </div>
              )}
            </div>
          ) : (
            <div>
              <video
                src={URL.createObjectURL(videoBlob)}
                controls
                style={{
                  width: '100%',
                  height: 400,
                  background: '#000',
                  borderRadius: '12px',
                  marginBottom: '1rem'
                }}
              />
              
              {isAnalyzing && (
                <div style={{
                  padding: '1rem',
                  background: COLORS.accentSoft,
                  borderRadius: '8px',
                  textAlign: 'center',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}>
                  <Brain size={16} style={{ color: COLORS.accent }} />
                  <strong style={{ color: COLORS.primary }}>{analysisStatus}</strong>
                  <div style={{ 
                    width: 16, 
                    height: 16, 
                    border: '2px solid transparent',
                    borderTop: `2px solid ${COLORS.primary}`,
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                </div>
              )}

              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <button
                  onClick={retakeVideo}
                  aria-label="Retake video"
                  disabled={isAnalyzing}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: 'transparent',
                    color: COLORS.text,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '8px',
                    fontWeight: 600,
                    cursor: isAnalyzing ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    opacity: isAnalyzing ? 0.5 : 1
                  }}
                >
                  <RotateCcw size={16} />
                  Retake Video
                </button>
                <button
                  onClick={handleVideoSubmit}
                  disabled={isAnalyzing}
                  aria-label="Submit video response"
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: isAnalyzing ? COLORS.neutral : assessmentConfig.color,
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 600,
                    cursor: isAnalyzing ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  {isAnalyzing ? (
                    <>
                      <Brain size={16} />
                      AI Analysis...
                    </>
                  ) : (
                    <>
                      <Send size={16} />
                      Submit Response
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render Written Question
  const renderWrittenQuestion = () => {
    const wordCount = writtenResponse.split(/\s+/).filter(word => word.length > 0).length;
    const minWords = 20;

    return (
      <div style={{
        background: COLORS.surface,
        padding: '2rem',
        borderRadius: '20px',
        boxShadow: '0 10px 32px rgba(44, 95, 63, 0.10)',
        border: `1px solid ${COLORS.border}`,
        marginBottom: '2rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '1.5rem' }}>
          <div style={{
            padding: '0.65rem',
            background: `${assessmentConfig.color}12`,
            borderRadius: '12px',
            color: assessmentConfig.color
          }}>
            <FileText size={20} />
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: 800,
              color: COLORS.text,
              margin: 0,
              lineHeight: 1.3
            }}>
              {currentQuestionData.phase} - Written Response
            </h2>
            <p style={{ color: COLORS.subtext, margin: '0.25rem 0 0 0' }}>
              Question {currentQuestion + 1} of {questions.length} • {formatTime(currentQuestionData.duration)} max
            </p>
          </div>
        </div>

        <div style={{
          background: COLORS.primarySoft,
          padding: '1.5rem',
          borderRadius: '12px',
          marginBottom: '2rem',
          border: `1px solid ${COLORS.border}`
        }}>
          <p style={{
            fontSize: '1.1rem',
            color: COLORS.text,
            margin: 0,
            lineHeight: 1.6,
            fontWeight: 600
          }}>
            {currentQuestionData.text}
          </p>
        </div>

        <div style={{ marginBottom: '2rem' }}>
          <textarea
            value={writtenResponse}
            onChange={(e) => setWrittenResponse(e.target.value)}
            placeholder="Type your response here... (Be detailed and specific)"
            aria-label="Written response"
            style={{
              width: '100%',
              minHeight: '200px',
              padding: '1rem',
              border: `2px solid ${COLORS.border}`,
              borderRadius: '8px',
              fontSize: '1rem',
              fontFamily: 'inherit',
              resize: 'vertical',
              background: COLORS.bg,
              transition: 'border-color 0.2s'
            }}
            onFocus={(e) => e.target.style.borderColor = assessmentConfig.color}
            onBlur={(e) => e.target.style.borderColor = COLORS.border}
          />
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginTop: '0.5rem'
          }}>
            <span style={{ 
              color: wordCount >= minWords ? COLORS.success : COLORS.subtext,
              fontSize: '0.9rem'
            }}>
              {wordCount} words {wordCount < minWords && `(minimum ${minWords} words)`}
            </span>
            {wordCount >= minWords && (
              <span style={{ color: COLORS.success, fontSize: '0.9rem' }}>
                <CheckCircle size={14} style={{ display: 'inline', marginRight: '0.25rem' }} />
                Ready to submit
              </span>
            )}
          </div>
        </div>

        {isAnalyzing && (
          <div style={{
            padding: '1rem',
            background: COLORS.accentSoft,
            borderRadius: '8px',
            textAlign: 'center',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem'
          }}>
            <Brain size={16} style={{ color: COLORS.accent }} />
            <strong style={{ color: COLORS.primary }}>{analysisStatus}</strong>
            <div style={{ 
              width: 16, 
              height: 16, 
              border: '2px solid transparent',
              borderTop: `2px solid ${COLORS.primary}`,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
          </div>
        )}

        <button
          onClick={handleWrittenSubmit}
          disabled={isAnalyzing || wordCount < minWords}
          aria-label="Submit written response"
          style={{
            padding: '0.75rem 1.5rem',
            background: (isAnalyzing || wordCount < minWords) ? COLORS.neutral : assessmentConfig.color,
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontWeight: 600,
            cursor: (isAnalyzing || wordCount < minWords) ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            opacity: (isAnalyzing || wordCount < minWords) ? 0.6 : 1,
            transition: 'all 0.2s'
          }}
        >
          {isAnalyzing ? (
            <>
              <Brain size={16} />
              AI Analysis...
            </>
          ) : (
            <>
              <Send size={16} />
              Submit Response
            </>
          )}
        </button>
      </div>
    );
  };

  // Render MCQ Question
  const renderMCQQuestion = () => {
    return (
      <div style={{
        background: COLORS.surface,
        padding: '2rem',
        borderRadius: '20px',
        boxShadow: '0 10px 32px rgba(44, 95, 63, 0.10)',
        border: `1px solid ${COLORS.border}`,
        marginBottom: '2rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '1.5rem' }}>
          <div style={{
            padding: '0.65rem',
            background: `${assessmentConfig.color}12`,
            borderRadius: '12px',
            color: assessmentConfig.color
          }}>
            <BookOpen size={20} />
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: 800,
              color: COLORS.text,
              margin: 0,
              lineHeight: 1.3
            }}>
              {currentQuestionData.phase} - Multiple Choice
            </h2>
            <p style={{ color: COLORS.subtext, margin: '0.25rem 0 0 0' }}>
              Question {currentQuestion + 1} of {questions.length}
            </p>
          </div>
        </div>

        <div style={{
          background: COLORS.primarySoft,
          padding: '1.5rem',
          borderRadius: '12px',
          marginBottom: '2rem',
          border: `1px solid ${COLORS.border}`
        }}>
          <p style={{
            fontSize: '1.1rem',
            color: COLORS.text,
            margin: 0,
            lineHeight: 1.6,
            fontWeight: 600
          }}>
            {currentQuestionData.text}
          </p>
        </div>

        <div style={{ marginBottom: '2rem' }}>
          {currentQuestionData.options.map((option, index) => (
            <div
              key={index}
              onClick={() => setSelectedMCQAnswer(index)}
              onKeyPress={(e) => e.key === 'Enter' && setSelectedMCQAnswer(index)}
              role="button"
              tabIndex={0}
              aria-label={`Option ${String.fromCharCode(65 + index)}: ${option}`}
              aria-pressed={selectedMCQAnswer === index}
              style={{
                padding: '1.25rem',
                marginBottom: '0.75rem',
                background: selectedMCQAnswer === index ? `${assessmentConfig.color}12` : COLORS.bg,
                border: `2px solid ${selectedMCQAnswer === index ? assessmentConfig.color : COLORS.border}`,
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '1rem'
              }}
              onMouseEnter={(e) => {
                if (selectedMCQAnswer !== index) {
                  e.currentTarget.style.borderColor = assessmentConfig.color;
                  e.currentTarget.style.background = `${assessmentConfig.color}05`;
                }
              }}
              onMouseLeave={(e) => {
                if (selectedMCQAnswer !== index) {
                  e.currentTarget.style.borderColor = COLORS.border;
                  e.currentTarget.style.background = COLORS.bg;
                }
              }}
            >
              <div style={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                border: `2px solid ${selectedMCQAnswer === index ? assessmentConfig.color : COLORS.neutral}`,
                background: selectedMCQAnswer === index ? assessmentConfig.color : 'transparent',
                flexShrink: 0,
                marginTop: '0.1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s'
              }}>
                {selectedMCQAnswer === index && (
                  <CheckCircle size={14} style={{ color: 'white' }} />
                )}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ 
                  fontWeight: 600, 
                  color: COLORS.text,
                  lineHeight: 1.5
                }}>
                  <strong style={{ color: assessmentConfig.color, marginRight: '0.5rem' }}>
                    {String.fromCharCode(65 + index)}.
                  </strong>
                  {option}
                </div>
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={handleMCQSubmit}
          disabled={selectedMCQAnswer === null}
          aria-label="Submit answer"
          style={{
            padding: '0.75rem 1.5rem',
            background: selectedMCQAnswer === null ? COLORS.neutral : assessmentConfig.color,
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontWeight: 600,
            cursor: selectedMCQAnswer === null ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            opacity: selectedMCQAnswer === null ? 0.6 : 1,
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            if (selectedMCQAnswer !== null) {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <Send size={16} />
          Submit Answer
        </button>
      </div>
    );
  };

  const renderQuestion = () => {
    if (!currentQuestionData) return null;

    switch (currentQuestionData.type) {
      case 'video':
        return renderVideoQuestion();
      case 'written':
        return renderWrittenQuestion();
      case 'mcq':
        return renderMCQQuestion();
      default:
        return null;
    }
  };

  // Render Results
  const renderResults = () => {
    return (
      <div style={{ background: COLORS.bg, minHeight: '100vh', padding: '2rem 1rem' }}>
        <GlobalStyles />
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          
          {/* Success Header */}
          <div style={{
            background: COLORS.surface,
            padding: '3rem',
            borderRadius: '24px',
            boxShadow: '0 12px 40px rgba(44, 95, 63, 0.15)',
            border: `2px solid ${assessmentConfig.color}30`,
            marginBottom: '2rem',
            textAlign: 'center'
          }}>
            <div style={{
              width: 100,
              height: 100,
              background: `${assessmentConfig.color}15`,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem'
            }}>
              <Award size={48} style={{ color: assessmentConfig.color }} />
            </div>
            
            <h1 style={{ 
              fontSize: '2.5rem', 
              fontWeight: 900, 
              color: assessmentConfig.color, 
              marginBottom: '1rem' 
            }}>
              Assessment Complete!
            </h1>
            
            <p style={{ 
              color: COLORS.subtext, 
              fontSize: '1.2rem',
              maxWidth: '600px',
              margin: '0 auto 2rem',
              lineHeight: 1.6
            }}>
              Your {assessmentConfig.name} assessment has been analyzed. Here are your personalized results and next steps.
            </p>

            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '1rem',
              background: `${assessmentConfig.color}15`,
              padding: '1rem 2rem',
              borderRadius: '50px',
              color: assessmentConfig.color,
              fontWeight: 800,
              fontSize: '1.1rem'
            }}>
              <CheckCircle2 size={24} />
              Ready for Matching & Next Steps
            </div>
          </div>

          {/* Results Summary */}
          <div style={{
            background: COLORS.surface,
            padding: '2.5rem',
            borderRadius: '20px',
            boxShadow: '0 10px 32px rgba(44, 95, 63, 0.10)',
            border: `1px solid ${COLORS.border}`,
            marginBottom: '2rem'
          }}>
            <h2 style={{
              fontSize: '1.75rem',
              fontWeight: 800,
              color: COLORS.text,
              marginBottom: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <assessmentConfig.icon size={28} style={{ color: assessmentConfig.color }} />
              Your {assessmentConfig.name} Profile
            </h2>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '1.5rem',
              marginBottom: '2rem'
            }}>
              <div style={{
                padding: '1.5rem',
                background: `${assessmentConfig.color}08`,
                borderRadius: '12px',
                border: `1px solid ${assessmentConfig.color}20`
              }}>
                <div style={{ color: assessmentConfig.color, fontWeight: 700, marginBottom: '0.5rem', fontSize: '0.9rem' }}>Assessment Type</div>
                <div style={{ color: COLORS.text, fontWeight: 600, fontSize: '1.1rem' }}>{assessmentConfig.name}</div>
              </div>
              <div style={{
                padding: '1.5rem',
                background: `${assessmentConfig.color}08`,
                borderRadius: '12px',
                border: `1px solid ${assessmentConfig.color}20`
              }}>
                <div style={{ color: assessmentConfig.color, fontWeight: 700, marginBottom: '0.5rem', fontSize: '0.9rem' }}>Questions Completed</div>
                <div style={{ color: COLORS.text, fontWeight: 600, fontSize: '1.1rem' }}>{questions.length} / {questions.length}</div>
              </div>
              <div style={{
                padding: '1.5rem',
                background: `${assessmentConfig.color}08`,
                borderRadius: '12px',
                border: `1px solid ${assessmentConfig.color}20`
              }}>
                <div style={{ color: assessmentConfig.color, fontWeight: 700, marginBottom: '0.5rem', fontSize: '0.9rem' }}>Next Step</div>
                <div style={{ color: COLORS.text, fontWeight: 600, fontSize: '1.1rem' }}>Founder/Student Matching</div>
              </div>
            </div>

            {/* Next Steps */}
            <div>
              <h3 style={{ color: COLORS.primary, marginBottom: '1rem', fontSize: '1.3rem' }}>What Happens Next?</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {[
                  "We'll analyze your assessment results with our AI matching algorithm",
                  "You'll receive personalized founder/student match recommendations",
                  "Attend our networking events to meet your matches",
                  "Start working on real projects together within 2 weeks"
                ].map((step, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '1rem',
                    padding: '1rem',
                    background: COLORS.primarySoft,
                    borderRadius: '8px',
                    border: `1px solid ${COLORS.border}`
                  }}>
                    <div style={{
                      width: 28,
                      height: 28,
                      background: assessmentConfig.color,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      flexShrink: 0
                    }}>
                      {index + 1}
                    </div>
                    <div style={{ color: COLORS.text, lineHeight: 1.6, paddingTop: '0.25rem' }}>{step}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => setCurrentPage && setCurrentPage('dashboard')}
              aria-label="View your matches"
              style={{
                padding: '1rem 2rem',
                background: assessmentConfig.color,
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '1rem',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <Users size={20} />
              View Your Matches
            </button>
            <button
              onClick={() => {
                setShowResults(false);
                setCurrentQuestion(0);
                setAssessmentData({ responses: [], analyses: [], currentPhase: '' });
                setError(null);
              }}
              aria-label="Retake assessment"
              style={{
                padding: '1rem 2rem',
                background: 'transparent',
                color: COLORS.text,
                border: `2px solid ${COLORS.border}`,
                borderRadius: '12px',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '1rem',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = assessmentConfig.color;
                e.currentTarget.style.color = assessmentConfig.color;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = COLORS.border;
                e.currentTarget.style.color = COLORS.text;
              }}
            >
              <RotateCcw size={20} />
              Retake Assessment
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Show landing page first
  if (showLanding) {
    return (
      <AssessmentLanding 
        onSelectAssessment={handleSelectAssessment}
        setCurrentPage={setCurrentPage}
      />
    );
  }

  // Show results if assessment is complete
  if (showResults) {
    return renderResults();
  }

  // Show assessment interface
  return (
    <div style={{ background: COLORS.bg, minHeight: '100vh', padding: '2rem 1rem' }}>
      <GlobalStyles />
      <div style={{ maxWidth: 800, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
          <button
            onClick={() => {
              setShowLanding(true);
              stopCamera();
            }}
            aria-label="Back to selection"
            style={{
              background: 'transparent',
              border: `2px solid ${COLORS.border}`,
              color: COLORS.text,
              padding: '0.65rem 1rem',
              borderRadius: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = assessmentConfig.color;
              e.currentTarget.style.color = assessmentConfig.color;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = COLORS.border;
              e.currentTarget.style.color = COLORS.text;
            }}
          >
            <ArrowRight size={16} style={{ transform: 'rotate(180deg)' }} />
            Back to Selection
          </button>

          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.6rem', 
            background: COLORS.surface, 
            padding: '0.55rem 0.9rem', 
            borderRadius: '12px', 
            border: `2px solid ${assessmentConfig.color}30` 
          }}>
            <assessmentConfig.icon size={16} style={{ color: assessmentConfig.color }} />
            <span style={{ color: assessmentConfig.color, fontWeight: 700 }}>
              {assessmentConfig.name}
            </span>
          </div>
        </div>

        {/* Progress */}
        <div style={{ 
          background: COLORS.surface, 
          padding: '1rem', 
          borderRadius: '16px', 
          border: `1px solid ${COLORS.border}`, 
          marginBottom: '2rem' 
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
            <span style={{ color: COLORS.text, fontWeight: 600 }}>
              {currentQuestionData.phase} • Question {currentQuestion + 1} of {questions.length}
            </span>
            <span style={{ color: assessmentConfig.color, fontWeight: 700 }}>
              {Math.round(((currentQuestion + 1) / questions.length) * 100)}%
            </span>
          </div>
          <div style={{ height: 8, background: COLORS.accentSoft, borderRadius: 999, overflow: 'hidden' }}>
            <div style={{
              width: `${((currentQuestion + 1) / questions.length) * 100}%`,
              height: '100%',
              background: `linear-gradient(135deg, ${assessmentConfig.color} 0%, ${assessmentConfig.color}AA 100%)`,
              borderRadius: 999,
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>

        {/* Current Question */}
        {renderQuestion()}
      </div>
    </div>
  );
}

export default AssessmentPage;
